import { DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';

export interface Step {
    screen: ScreenType;
    trigger: ScreenTrigger;
    next_screen: ScreenType;
    update_timestamp: string; // date-time
    id: string; // uuid
    account_id: string; // uuid
    wizard_id: string; // uuid
    creation_timestamp: string; // date-time
}

export interface CreateStep {
    screen?: ScreenType;
    trigger?: ScreenTrigger;
    next_screen?: ScreenType;
}

export interface UpdateStep {
    null_fields?: string[] | null;
    screen?: ScreenType | null;
    trigger?: ScreenTrigger | null;
    next_screen?: ScreenType | null;
}

export enum ScreenType {
    AUTH_QR_CODE = 'auth_qr_code',
    ADD_DESTINATION_SLOT_CONSTRAINT = 'add_destination_slot_constraint',
    ADD_SIGNATURE_TO_TRANSACTION = 'add_signature_to_transaction',
    CAROUSEL = 'carousel',
    CONFIRM_CURRENT_LOCATION = 'confirm_current_location',
    CONFIRM_CURRENT_DEVICE_LOCATION = 'confirm_current_device_location',
    DETECT_CURRENT_DEVICE_LOCATION = 'detect_current_device_location',
    CONTINUE_TO_LOCATION = 'continue_to_location',
    SCANNER = 'scanner',
    ENTER_PURCHASE_ORDER = 'enter_purchase_order',
    ENTER_PARCEL_INFORMATION = 'enter_parcel_information',
    ENTER_RECEIVER_SUPPLIER = 'enter_receiver_supplier',
    ENTER_PRODUCT_INFORMATION = 'enter_product_information',
    ENTER_PRODUCT_CATEGORY = 'enter_product_category',
    ENTER_PRODUCT_TRANSLATIONS = 'enter_product_translations',
    PRINT_LABELS = 'print_labels',
    PROCESS_OPEN_CLOSE_SLOT = 'process_open_close_slot',
    PROCESS_OPEN_CLOSE_SLOT_DROP_OFF = 'process_open_close_slot_drop_off',
    PROCESS_OPEN_CLOSE_SLOT_PICK_UP = 'process_open_close_slot_pick_up',
    PROCESS_OPEN_CLOSE_SLOT_SHOP = 'process_open_close_slot_shop',
    PROCESS_OPEN_CLOSE_SLOT_RETURN = 'process_open_close_slot_return',
    PROCESS_OPEN_CLOSE_SLOT_REVIEW = 'process_open_close_slot_review',
    PROCESS_OPEN_CLOSE_SLOTS = 'process_open_close_slots',
    EDIT_TRANSACTION_COMMENT_AND_PICTURES = 'edit_transaction_comment_and_pictures',
    DROP_OFF_METHOD = 'drop_off_method',
    AUTHENTICATED_DROP_OFF_METHOD = 'authenticated_drop_off_method',
    DROP_OFF_PIN_CODE = 'drop_off_pin_code',
    DROP_OFF_SCAN_QR_CODE = 'drop_off_scan_qr_code',
    DROP_OFF_CONFIRM_OPEN_SLOT = 'drop_off_confirm_open_slot',
    DROP_OFF_CONFIRM_OPEN_SLOTS_PARALLEL = 'drop_off_confirm_open_slots_parallel',
    DROP_OFF_CONFIRM_OPEN_SLOTS_SERIAL = 'drop_off_confirm_open_slots_serial',
    ITSME_QR_AUTHENTICATION = 'itsme_qr_authentication',
    OPEN_DROP_OFF_SCAN_QR_CODE = 'open_drop_off_scan_qr_code',
    OPEN_DROP_OFF_SCAN_COURIER_TRACK_AND_TRACE = 'open_drop_off_scan_courier_track_and_trace',
    OPEN_DROP_OFF_CONFIRM_OPEN_SLOT = 'open_drop_off_confirm_open_slot',
    FABLAB_SCAN_QR_CODE = 'fablab_scan_qr_code',
    FABLAB_SELECT_RECEIVERS = 'fablab_select_receivers',
    FABLAB_DROP_OFF_CONFIRM_OPEN_SLOT = 'fablab_drop_off_confirm_open_slot',
    CREATE_RECEIVER = 'create_receiver',
    SET_CURRENT_USER_AS_SENDER = 'set_current_user_as_sender',
    SELECT_COURIER = 'select_courier',
    SELECT_RECEIVERS = 'select_receivers',
    SELECT_GROUPED_RECEIVERS = 'select_grouped_receivers',
    SELECT_SENDERS = 'select_senders',
    SELECT_SENDERS_AND_RECEIVERS = 'select_senders_and_receivers',
    SELECT_SENDERS_AND_RECEIVERS_WITH_PROOF_OF_IDENTITY = 'select_senders_and_receivers_with_proof_of_identity',
    SELECT_LOCATION = 'select_location',
    SELECT_ORDER_DELIVERY_LOCATION = 'select_order_delivery_location',
    SELECT_LOCATION_SLOT_REQUIRED = 'select_location_slot_required',
    SELECT_SLOT_SIZE = 'select_slot_size',
    DROP_OFF = 'drop_off',
    DROP_OFF_CONFIRMED = 'drop_off_confirmed',
    CONFIRM_DROP_OFF = 'confirm_drop_off',
    CONFIRM_TRANSACTION = 'confirm_transaction',
    CONFIRM_TRANSACTION_PRINT_LABELS = 'confirm_transaction_print_labels',
    TRANSACTION_OVERVIEW = 'transaction_overview',
    PROVE_IDENTITY = 'prove_identity',
    PROVE_IDENTITY_BADGE = 'prove_identity_badge',
    PROVE_IDENTITY_ITSME = 'prove_identity_itsme',
    CREATE_AUDIT_TRANSACTION = 'create_audit_transaction',
    PICK_UP = 'pick_up',
    PICK_UP_CONFIRMED = 'pick_up_confirmed',
    PICK_UP_CONFIRM_OPEN_SLOT = 'pick_up_confirm_open_slot',
    PICK_UP_CONFIRM_OPEN_SLOTS_PARALLEL = 'pick_up_confirm_open_slots_parallel',
    PICK_UP_CONFIRM_OPEN_SLOTS_SERIAL = 'pick_up_confirm_open_slots_serial',
    PICK_UP_METHOD = 'pick_up_method',
    AUTHENTICATED_PICK_UP_METHOD = 'authenticated_pick_up_method',
    PICK_UP_OVERVIEW = 'pick_up_overview',
    PICK_UP_PIN_CODE = 'pick_up_pin_code',
    PICK_UP_SCAN_PARCELS = 'pick_up_scan_parcels',
    PICK_UP_SCAN_QR_CODE = 'pick_up_scan_qr_code',
    PICK_UP_SCAN_MULTIPLE_PARCELS = 'pick_up_scan_multiple_parcels',
    CONFIRM_PICK_UP_MULTIPLE_PARCELS = 'confirm_pick_up_multiple_parcels',
    DROP_OFF_SCAN_MULTIPLE_PARCELS = 'drop_off_scan_multiple_parcels',
    CONFIRM_DROP_OFF_MULTIPLE_PARCELS = 'confirm_drop_off_multiple_parcels',
    PICK_UP_BY = 'pick_up_by',
    START_SCREEN = 'start_screen',
    PREVIOUS = 'previous',
    HOME = 'home',
    CONFIRM_PICK_UP = 'confirm_pick_up',
    MANAGE_GATES = 'manage_gates',
    MOVE_PARCELS = 'move_parcels',
    CONFIRM_MOVE_PARCELS = 'confirm_move_parcels',
    BROWSE_PRODUCTS_BY_CATEGORIES = 'browse_products_by_categories',
    SELECT_ATTRIBUTES = 'select_attributes',
    SELECT_QUANTITY = 'select_quantity',
    SELECT_COST_CENTER = 'select_cost_center',
    CONSUME_KIT = 'consume_kit',
    SELECT_RECEIVER_SPOT = 'select_receiver_spot',
    ENTER_TRACKING_NUMBER = 'enter_tracking_number',
    ENTER_SHIPPING_NOTES = 'enter_shipping_notes',
    ENTER_TRACKING_NUMBER_AND_SHIPPING_NOTES = 'enter_tracking_number_and_shipping_notes',
    CONFIRM_LENDING = 'confirm_lending',
    CONFIRM_SHOP_TRANSACTION = 'confirm_shop_transaction',
    LENDING_RETURN_OVERVIEW = 'lending_return_overview',
    LENDING_RETURN_AVAILABLE_DETAIL = 'lending_return_available_detail',
    LENDING_REVIEW_OVERVIEW = 'lending_review_overview',
    LENDING_REVIEW_DETAIL = 'lending_review_detail',
    LENDING_REVIEW_RESOLVE = 'lending_review_resolve',
    LENDING_PICK_BY_STATE = 'lending_pick_by_state',
    LICENSE_BUTTONS = 'license_buttons',
    VENDING_RETURN_OVERVIEW = 'vending_return_overview',
    VERIFY_CURRENT_LOCATION = 'verify_current_location',
    INSERT_CURRENT_LOCATION = 'insert_current_location',
    IDENTIFY_CURRENT_LOCATION = 'identify_current_location',
    IDENTIFY_USER = 'identify_user',
    REMOVE_DESTINATION_SLOT_CONSTRAINT = 'remove_destination_slot_constraint',
    RETURN_OVERVIEW = 'return_overview',
    RETURN_DETAIL = 'return_detail',
    REVIEW_RETURNS = 'review_returns',
    REVIEW_DETAIL = 'review_detail',
    REVIEW_RESOLVE = 'review_resolve',
    LENDING_RETURN_DETAIL = 'lending_return_detail',
    VENDING_RETURN_DETAIL = 'vending_return_detail',
    KIT_RETURN_METHOD = 'kit_return_method',
    CONFIRM_KIT_RETURN = 'confirm_kit_return',
    CONFIRM_KIT_RETURN_SLOT = 'confirm_kit_return_slot',
    RETURN_EQUIPMENT_LIST = 'return_equipment_list',
    EQUIPMENT_RETURN_LOST = 'equipment_return_lost',
    WEARABLE_RETURN_REASON = 'wearable_return_reason',
    REQUEST_SERVICES = 'request_services',
    VERIFY_CONTACT_INFORMATION = 'verify_contact_information',
    CONFIRM_RETURN = 'confirm_return',
    REFILL_OVERVIEW = 'refill_overview',
    REFILL_DETAIL = 'refill_detail',
    CONFIRM_REFILL = 'confirm_refill',
    REPLENISH_OVERVIEW = 'replenish_overview',
    REPLENISH_DETAIL = 'replenish_detail',
    CONFIRM_REPLENISH = 'confirm_replenish',
    PROCESS_OPEN_CLOSE_SPOT_LAYOUT = 'process_open_close_spot_layout',
    CONFIRM_ORDER = 'confirm_order',
    BASKET_OVERVIEW = 'basket_overview',
    RECYCLE_OVERVIEW = 'recycle_overview',
    CREATE_RECYCLE_TRANSACTION = 'create_recycle_transaction',
    REVIEW_RECYCLES = 'review_recycles',
    REVIEW_RECYCLES_CONFIRM_OPEN_SLOT = 'review_recycles_confirm_open_slot',
    RESET_BADGE = 'reset_badge',
    RESET_BADGE_CONFIRMED = 'reset_badge_confirmed',
    LINK_BADGE = 'link_badge',
    LINK_BADGE_CONFIRMED = 'link_badge_confirmed',
    IDENTIFY_BADGE = 'identify_badge',
    MOVE_SLOT_CONTENTS = 'move_slot_contents',
    CONFIRM_MOVE_SLOT_CONTENTS = 'confirm_move_slot_contents',
    MOVE_SLOT_CONTENTS_CONFIRMED = 'move_slot_contents_confirmed',
    HAND_OFF = 'hand_off',
    CONFIRM_HAND_OFF = 'confirm_hand_off',
    HAND_OFF_CONFIRMED = 'hand_off_confirmed',
    TRANSACTION_METHOD = 'transaction_method',
    TRANSACTION_SCHEDULING = 'transaction_scheduling',
    VERIFY_ORDER_STOCK_REQUIREMENT = 'verify_order_stock_requirement'
}

export enum ScreenTrigger {
    BACK = 'back',
    BACK_TO = 'back_to',
    CANCEL = 'cancel',
    CLOSE = 'close',
    CONTINUE_TO = 'continue_to',
    HOME = 'home',
    LOGOUT = 'logout',
    SCAN_CODE = 'scan_code',
    SUBMIT_FALSE = 'submit_false',
    SUBMIT = 'submit',
    SUBMIT_PIN_CODE = 'submit_pin_code',
    SUBMIT_TRUE = 'submit_true'
}

enum QueryParams {
    account_id = 'account_id',
    wizard_id = 'wizard_id'
}
export type StepQueryParams = DefaultQueryParams & QueryParams;
export type StepListResponse = PaginatedResponse<Step>;
